import { HighlightIcon } from '@dev-spendesk/grapes';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import classNames from 'classnames';

import { useFeature } from 'src/core/common/hooks/useFeature';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import FEATURES from 'src/core/constants/features';

import { type ApprovalSchemeWithMemberNames } from '../../hooks/useApprovalSchemesQuery';
import { useRegisterNodeHeight } from '../../hooks/useRegisterNodeHeight';
import { useWorkflowContext } from '../../state/WorkflowContext';
import { ApprovalSchemePreview } from '../ApprovalSchemePreview/ApprovalSchemePreview';

export type StandardApprovalFlowNodeData = {
  schemeId: string;
  approvalScheme?: ApprovalSchemeWithMemberNames;
};

const handleStyle = {
  background: 'transparent',
  border: 'none',
  width: 0,
  height: 0,
};

type Props = NodeProps & {
  data: StandardApprovalFlowNodeData;
};

export const StandardApprovalFlowNode = ({ id, data }: Props) => {
  const { t } = useTranslation('global');
  const { setSelectedNodeId, selectedNodeId, workflowView } =
    useWorkflowContext();
  const isTemporaryApprovalWorkflowSchemePreviewEnabled = useFeature(
    FEATURES.TMP_APPROVAL_WORKFLOW_SCHEME_PREVIEW,
  );
  const isSelected = selectedNodeId === id;
  const nodeReference = useRegisterNodeHeight<HTMLDivElement>(id);

  return (
    <div ref={nodeReference} className="flex items-center">
      <button
        onClick={() => {
          setSelectedNodeId(id, true);
        }}
        type="button"
        className={classNames(
          'flex w-[320px] flex-col justify-center rounded-12 border border-solid p-16 text-left elevation-s',
          isSelected
            ? 'border-border-default bg-background-primary-default outline outline-offset-[--grapes-focus-ring-offset] outline-[--grapes-color-background-primary-brand-default]'
            : 'cursor-pointer border-border-default bg-background-primary-default hover:bg-background-primary-hover',
        )}
      >
        <div className="flex items-center gap-16">
          <HighlightIcon variant="ocean" name="magic-wand" size={24} />
          <div className="text-content-primary title-m">
            {t('approvalWorkflows.standardApprovalFlow.defaultApprovers')}
          </div>
        </div>
        {isTemporaryApprovalWorkflowSchemePreviewEnabled &&
          workflowView === 'extended' &&
          data.approvalScheme && (
            <>
              <div className="separator my-16" />
              <ApprovalSchemePreview approvalScheme={data.approvalScheme} />
            </>
          )}
      </button>
      <Handle style={handleStyle} type="target" position={Position.Left} />
    </div>
  );
};
